import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import HelpIcon from '@material-ui/icons/Help';
import { makeStyles, Button, Tooltip } from '@material-ui/core';
import map from 'lodash/map';

import { getI18n } from "../../../utils/intl";
import FormTextField from '../../FormTextField';
import styles from './styles';

const FilterByURI = (props) => {
  const { isGrouped, updateData } = props;

  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const validateURI = value => {
      const pattern = new RegExp(
        '^\\/?((([0-9A-Za-z-_])|(\\[[wd]\\]))\\/?)*(\\[x\\])?$',
      );
      return value.length && pattern.test(value);
    };

  const validateNameSplChars = name => new RegExp('^[0-9A-Za-z-_[\\]()\\/ ]*$').test(name);
  const validateNameLength = name => name.length > 0 && name.length < 61;
  const validateNameDuplicate = (name, data) =>
    data.filter(entry => entry.name === name).length === 1;

  const validateName = (name, data) => {
    let message;
    let error = false;
    if (!validateNameDuplicate(name, data)) {
      message = intl.getI18nMessage('label.analytics.customReport.dashboard.label.duplicate.name.error.text');
      error = true;
    } else if (!validateNameSplChars(name)) {
      message = intl.getI18nMessage('label.analytics.customReport.dashboard.label.splchars.name.error.text');
      error = true;
    } else if (name.length !== name.trim().length) {
      message = intl.getI18nMessage('label.analytics.customReport.dashboard.label.invalid.name.error.text');
      error = true;
    } else {
      message = `${name.length}/60 Characters`;
      error = !validateNameLength(name);
    }
    return { message, error };
  }

  const isAnEntryInvalid = data => (
    data && data.length ? data.find(
      entry => !validateURI(entry.value) || validateName(entry.name, data).error,
    ) : true);

  const [state, updateState] = useState({
    data: props.data && props.data.length ? props.data : [{ name: '', value: '' }],
    invalidEntry: isAnEntryInvalid(props.data),
  });

  const setState = (value) => {
    if (typeof value === 'function') {
      updateState(currentValue => ({ ...currentValue, ...value(state) }));
    } else {
      updateState(currentValue => ({ ...currentValue, ...value }));
    }
  }

  const isLoadingRef = useRef(true);
  useEffect(() => {
    isLoadingRef.current = false;
  }, []);

  const handleNameChange = (value, index) => {
    handleChange(value, index, true);
  };
  
  const handleValueChange = (value, index) => {
    handleChange(value, index, false);
  };
  
  const handleChange = (value, index, isName) => {
    if (!isLoadingRef.current) {
      const data = state.data;
      if (isName) {
        data[index].nameChanged = true;
        data[index].name = value;
      } else {
        data[index].valChanged = true;
        data[index].value = value;
      }
      const invalidEntry = isAnEntryInvalid(data);
      setState({ invalidEntry, data });
      updateData(invalidEntry, data);
    }
  };
  
  const addValue = () => {
    const updatedData = [
      ...state.data,
      { name: '', value: '', nameChanged: false, valChanged: false },
    ];
    setState({
      invalidEntry: true,
      data: updatedData,
    });
    updateData(true, updatedData);
  };
  
  const removeValue = index => {
    const data = state.data.filter((_, i) => i !== index);
    const invalidEntry = isAnEntryInvalid(data);
    setState({ data, invalidEntry });
    updateData(invalidEntry, data);
  };

  return (
    <div className={classes.filterByURIContainer}>
      {map(state.data, (entry, index) => (
        <div className={classes.valueContainer} key={index}>
          <div className={classes.nameAndRemove}>
            <div className={classes.nameComponent}>
              <div className={classes.smallLabelAbove}>
                {' '}
                {intl.getI18nMessage('label.analytics.filter.type.filterByName.label')}
              </div>
                <FormTextField
                  name={`nameField-${index}`}
                  id={`nameField-${index}`}
                  fieldContainerClass={classes.inputFiledContainer}
                  hideLabel
                  labelClass={classes.textFieldLabel}
                  value={entry.name}
                  handleChange={(val) => handleNameChange(val, index)}
                  maxLength={60}
                />
              <div
                className={
                  entry.nameChanged &&
                  validateName(entry.name, state.data).error
                    ? classes.smallLabelBelowError
                    : classes.smallLabelBelow
                }
              >
                {validateName(entry.name, state.data).message}
              </div>
            </div>

            {state.data.length > 1 ? (
              <Button
                color="primary"
                className={classes.removeValueButton}
                onClick={() => removeValue(index)}
                data-apim-test={'remove-button'}
                variant="outlined"
              >
                Remove
              </Button>
            ) : null}
          </div>
          <div className={classes.valueComponent}>
            <div className={classes.expressionWithToolTip}>
              <div className={classes.smallLabelAbove}>
                {intl.getI18nMessage('label.analytics.filter.type.filterByUriExpression.label')}
              </div>
              <Tooltip
                classes={classes}
                title={<div>
                    <h3>
                      {intl.getI18nMessage('label.analytics.customReport.dashboard.uri.tooltip.help.title.text')}
                    </h3>
                    <p>
                      {intl.getI18nMessage('label.analytics.customReport.dashboard.uri.tooltip.help.desc.text')}
                    </p>
                    <p>
                      {intl.getI18nMessage('label.analytics.customReport.dashboard.uri.tooltip.help.patterns.text')}
                    </p>
                    <ul className={classes.tooltipList}>
                      <li>
                        {intl.getI18nMessage('label.analytics.customReport.dashboard.uri.tooltip.help.patterns.text.1')}
                        {intl.getI18nMessage('label.analytics.customReport.dashboard.uri.tooltip.help.patterns.text.1.1')}
                      </li>
                      <li>
                        {intl.getI18nMessage('label.analytics.customReport.dashboard.uri.tooltip.help.patterns.text.2')}
                        {intl.getI18nMessage('label.analytics.customReport.dashboard.uri.tooltip.help.patterns.text.2.1')}
                      </li>
                      <li>
                        {intl.getI18nMessage('label.analytics.customReport.dashboard.uri.tooltip.help.patterns.text.3')}
                        {intl.getI18nMessage('label.analytics.customReport.dashboard.uri.tooltip.help.patterns.text.3.1')}
                      </li>
                    </ul>
                </div>}
                arrow
                placement="right"
              >
                <HelpIcon className={classes.helpIcon}/>
              </Tooltip>
            </div>
              <FormTextField
                hideLabel
                labelClass={classes.textFieldLabel}
                name={`valueField-${index}`}
                id={`valueField-${index}`}
                fieldContainerClass={classes.inputFiledContainer}
                handleChange={(val) => handleValueChange(val, index)}
                value={entry.value}
              />
            {entry.valChanged && !validateURI(entry.value) ? (
              <p className={classes.errorMessage}>
                {intl.getI18nMessage('label.analytics.customReport.dashboard.label.expression.error.text')}
              </p>
            ) : null}
          </div>
        </div>
      ))}
      {state.data.length < 10 || isGrouped ? (
        <Button
          className={state.invalidEntry ?
            clsx(classes.addValueButton, classes.addValueButtonDisabled)
            :
            classes.addValueButton
          }
          onClick={addValue}
          data-apim-test={'add - another - button'}
          disabled={state.invalidEntry}
        >
          Add Another
        </Button>
      ) : null}
    </div>
  );
};

FilterByURI.propTypes = {
  data: PropTypes.array,
  updateData: PropTypes.func,
  isGrouped: PropTypes.bool,
};

export default FilterByURI;